import { COMPANY, TEAM } from "@/data/products";
import { MapPin, Building2, Mail, MessageCircle } from "lucide-react";

const ABOUT_IMG =
  "https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg";
const LOGISTICS_IMG =
  "https://images.pexels.com/photos/11666903/pexels-photo-11666903.jpeg";

export default function About() {
  return (
    <div data-testid="page-about" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <p className="label-eyebrow">/ About EELLEVEN Mining</p>
      <h1 className="font-editorial mt-4 text-4xl md:text-6xl lg:text-7xl leading-[1.02]">
        A Zambian Mining &
        <span className="font-editorial-italic copper-text"> Trade House.</span>
      </h1>
      <p className="mt-4 label-eyebrow text-zinc-500">
        Strength. Strategy. Sustainable Growth.
      </p>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7 space-y-6 text-zinc-300 leading-relaxed font-light">
          <p>
            EELLEVEN Mining is a Zambia-registered mining and commodities
            trading company headquartered in Kabwe, operating from our
            central export yard. We specialize in the sourcing,
            processing, and export of high-quality mineral commodities from
            Zambia to international markets.
          </p>

          <div>
            <p className="font-heading text-white font-semibold mb-3">
              Our product portfolio includes:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {[
                "Copper Cathodes",
                "Copper Wire Scrap",
                "Copper Concentrate",
                "Copper Ore",
                "Cobalt Ore",
                "Manganese Ore",
                "Coal",
              ].map((item) => (
                <li
                  key={item}
                  data-testid={`about-product-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="flex items-start gap-2"
                >
                  <span className="w-1.5 h-1.5 mt-2 copper-bg shrink-0" />
                  <span className="text-zinc-200">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <p>
            We supply directly to refineries, smelters, foundries,
            manufacturers, and licensed international commodity traders
            worldwide.
          </p>

          <h2 className="font-editorial text-2xl md:text-3xl text-white mt-10 pt-2">
            A Transparent
            <span className="font-editorial-italic copper-text"> Supply Chain.</span>
          </h2>
          <p>
            Our business model is built on transparency, operational excellence,
            and reliable execution. We source mineral products from licensed
            Zambian mining operations, consolidate inventory at our Kabwe
            logistics yard, conduct grading and weighing on-site, and coordinate
            export logistics through established transport corridors to
            international ports.
          </p>
          <p>
            By maintaining strict oversight throughout the supply chain, we
            ensure consistent product quality, accurate documentation, efficient
            logistics, and dependable shipment schedules.
          </p>

          <h2 className="font-editorial text-2xl md:text-3xl text-white mt-10 pt-2">
            Reliable Supply.
            <span className="font-editorial-italic copper-text">
              {" "}
              Long-Term Partnerships.
            </span>
          </h2>
          <p>
            EELLEVEN Mining maintains export-ready inventory and is positioned
            to fulfill both spot purchases and long-term supply agreements. We
            are committed to building lasting partnerships through consistent
            product quality, transparent business practices, competitive
            pricing, and reliable delivery.
          </p>
          <p>
            We welcome direct engagement from qualified end-buyers, including
            refineries, smelters, foundries, manufacturers, and licensed
            international commodity trading companies seeking a trusted Zambian
            supply partner.
          </p>

          <div className="border border-zinc-800 mt-10 grid grid-cols-2 md:grid-cols-3">
            {[
              ["HQ", "Kabwe, Zambia"],
              ["Specialty", "Multi-Mineral"],
              ["Ports", "DAR / WVB / BEW"],
              ["Incoterms", "FOB / CFR"],
              ["Payment", "TT / Escrow"],
              ["Inspection", "SGS / Bureau Veritas"],
            ].map(([k, v], i) => (
              <div
                key={i}
                className={`p-5 ${
                  i % 3 !== 2 ? "md:border-r" : ""
                } ${i < 3 ? "border-b" : ""} border-zinc-800`}
                data-testid={`about-meta-${i}`}
              >
                <p className="label-eyebrow text-zinc-500">{k}</p>
                <p className="mt-2 font-heading font-bold text-base">{v}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="border border-zinc-800 overflow-hidden">
            <img
              src={ABOUT_IMG}
              alt="Warehouse"
              className="w-full h-72 object-cover"
            />
          </div>
          <div className="border border-zinc-800 overflow-hidden">
            <img
              src={LOGISTICS_IMG}
              alt="Loading dock"
              className="w-full h-56 object-cover"
            />
          </div>
        </div>
      </div>

      {/* Operational Sites */}
      <div className="mt-24 border-t border-zinc-800 pt-16">
        <p className="label-eyebrow">/ Footprint</p>
        <h2 className="font-heading mt-3 text-3xl md:text-5xl font-black uppercase tracking-tighter">
          Our yard in Zambia.
        </h2>
        <p className="mt-4 max-w-2xl text-zinc-400">
          Headquartered in Kabwe — our central export yard positioned for
          sourcing, consolidation and export.
        </p>

        <div className="mt-10 grid grid-cols-1 gap-px bg-zinc-800 border border-zinc-800">
          {COMPANY.sites.map((s, i) => (
            <div
              key={s.name}
              data-testid={`about-site-${i}`}
              className="bg-[var(--eel-surface)] p-7"
            >
              <div className="flex items-center gap-3">
                <Building2 size={22} className="copper-text" />
                <span className="label-eyebrow text-zinc-500">
                  {String(i + 1).padStart(2, "0")} · {s.label}
                </span>
              </div>
              <h3 className="mt-4 font-heading text-2xl font-black uppercase tracking-tight">
                {s.name}
              </h3>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed flex items-start gap-2">
                <MapPin size={14} className="mt-0.5 copper-text shrink-0" />
                {s.address}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Leadership / Meet the Team */}
      <div
        className="mt-24 border-t border-zinc-800 pt-16"
        data-testid="about-leadership"
      >
        <p className="label-eyebrow">/ Leadership</p>
        <h2 className="font-editorial mt-3 text-3xl md:text-5xl leading-[1.04]">
          Meet the team that
          <span className="font-editorial-italic copper-text">
            {" "}
            leads EELLEVEN.
          </span>
        </h2>
        <p className="mt-4 max-w-2xl text-zinc-400 font-light">
          The people behind our operations, trade desk and global partnerships.
        </p>

        {/* Featured MD cards — hero-style, one per MD */}
        <div className="mt-12 space-y-10">
          {TEAM.map((m, idx) => (
            <article
              key={m.name}
              data-testid={`about-team-${idx}`}
              className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-zinc-800 overflow-hidden bg-[var(--eel-surface)]"
            >
              <div className="lg:col-span-5 relative aspect-[4/5] lg:aspect-auto overflow-hidden bg-zinc-900">
                <img
                  src={m.photo}
                  alt={m.name}
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="lg:col-span-7 p-8 md:p-12 lg:p-14 flex flex-col justify-center">
                <p className="label-eyebrow copper-text">{m.role}</p>
                <h3
                  className="mt-4 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-white"
                  data-testid={`team-name-${idx}`}
                >
                  {m.name}
                </h3>
                <p className="mt-6 text-base md:text-lg text-zinc-300 leading-relaxed font-light">
                  {m.bio}
                </p>

                {m.focus && (
                  <ul className="mt-7 flex flex-wrap gap-2">
                    {m.focus.map((f, j) => (
                      <li
                        key={j}
                        className="text-[10px] uppercase tracking-widest border border-zinc-700 px-3 py-1.5 text-zinc-200"
                      >
                        {f}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-8 pt-8 border-t border-zinc-800 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <a
                    href={`mailto:${m.email}`}
                    data-testid={`team-email-${idx}`}
                    className="flex items-center gap-3 group"
                  >
                    <span className="w-10 h-10 grid place-items-center border border-zinc-800 group-hover:border-[var(--eel-copper)] transition shrink-0">
                      <Mail size={14} className="copper-text" />
                    </span>
                    <span>
                      <span className="label-eyebrow text-zinc-500 block">Email</span>
                      <span className="text-zinc-200 group-hover:text-[var(--eel-copper)] transition break-all">
                        {m.email}
                      </span>
                    </span>
                  </a>
                  <a
                    href={`https://wa.me/${m.whatsappDigits}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`team-wa-ie-${idx}`}
                    className="flex items-center gap-3 group"
                  >
                    <span className="w-10 h-10 grid place-items-center border border-zinc-800 group-hover:border-green-500 transition shrink-0">
                      <MessageCircle size={14} className="text-green-400" />
                    </span>
                    <span>
                      <span className="label-eyebrow text-zinc-500 block">
                        WhatsApp · IE
                      </span>
                      <span className="text-zinc-200 group-hover:text-green-400 transition">
                        {m.whatsapp}
                      </span>
                    </span>
                  </a>
                  <a
                    href={`https://wa.me/${m.phoneDigits}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`team-wa-zm-${idx}`}
                    className="flex items-center gap-3 group"
                  >
                    <span className="w-10 h-10 grid place-items-center border border-zinc-800 group-hover:border-green-500 transition shrink-0">
                      <MessageCircle size={14} className="text-green-400" />
                    </span>
                    <span>
                      <span className="label-eyebrow text-zinc-500 block">
                        WhatsApp · ZM
                      </span>
                      <span className="text-zinc-200 group-hover:text-green-400 transition">
                        {m.phone}
                      </span>
                    </span>
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
